import {HeaderTitle, Icon} from '@/components/atoms'
import {CustomDataTable, UploadHistoryTableModal} from '@/components/organisms'
import useUploadHistory from '@/hooks/useUploadHistory'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {UPLOAD_HISTORY_TYPE} from '@/utils/constants/globalConstants'
import {Header} from '@/utils/constants/header'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useMemo, useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {MAIN_COLUMNS, SUB_COLUMNS, UI_CONSTANTS} from './InventoryListConstants'
import {useInventory} from './useInventory'
import {useInventoryActions} from './useInventoryActions'

const InventoryList = () => {
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  const [rowSelected, setRowSelected] = useState([])
  const navigate = useNavigate()
  const [isSelectAll, setSelectAll] = useState(false)

  const {
    inventoryData,
    inventoryColConfigData,
    setSortingObj,
    setSearchArry,
    onPageIndexChange,
    onDownloadBtnClick,
    inventoryResult,
    createSearchArray,
    resetAllBtnClicked,
    fetchUploadErrors,
  } = useInventory({isSelectAll})
  const {onUploadHistoryPageIndexChange, uploadHistoryData, downloadUploadError} = useUploadHistory(
    UPLOAD_HISTORY_TYPE?.INVENTORY
  )

  // Use custom hook for business logic separation
  const {handleResetAll} = useInventoryActions({
    inventoryData,
    rowSelected,
    onDownloadBtnClick,
    setSortingObj,
    setSearchArry,
    setRowSelected,
    createSearchArray,
  })

  // Transform columns to CustomDataTable format
  const transformedColumns = useMemo(
    () =>
      inventoryColConfigData.map(col => ({
        name_of_db_column: col.name_of_db_column,
        label_of_column: col.label_of_column,
        filterVariant: col.filterVariant || 'text',
        // Add clickable configuration for productId
        ...(col.name_of_db_column === 'productId' && {
          isClickable: true,
          onCellClick: value => {
            if (value) {
              navigate(ROUTES.PRODUCT_REVIEW.replace(':id', value))
            }
          },
        }),
      })),
    [inventoryColConfigData, navigate]
  )

  // Handle sorting for CustomDataTable
  const handleSorting = ({column, direction}) => {
    setSortingObj({column, direction})
  }

  // Handle row selection for CustomDataTable
  const handleRowSelect = selectedRows => {
    setRowSelected(selectedRows)
  }

  // Handle page index change for CustomDataTable
  const handlePageIndexChange = pageIndex => {
    onPageIndexChange(pageIndex)
  }

  // Define the actions dynamically
  const tableActions = useMemo(
    () => [
      {
        id: 'view-upload-history',
        label: 'View Uploaded History',
        onClick: () => setOpenUploadHistory(true),
      },
      {
        divider: true, // This creates a separator
      },
      {
        id: 'download',
        label: 'Download',
        onClick: ({selectedRows = []} = {}) => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelected
          const dataToDownload = selectedRows.length > 0 ? selectedRows : rowSelected
          if (dataToDownload.length > 0) {
            onDownloadBtnClick(dataToDownload)
          }
        },
        disabled: ({selectedRows}) => !selectedRows?.length, // Dynamic disable based on selection
      },
    ],
    [onDownloadBtnClick, rowSelected]
  )

  const columns = MAIN_COLUMNS(downloadUploadError)

  // Determine if any sub row has locationId
  const allRows = (uploadHistoryData?.data?.rows || uploadHistoryData?.data || []);
  const hasLocationId = allRows.some(row => !!row.locationId);

  // Build subColumns dynamically
  const dynamicSubColumns = useMemo(() => [
    { columnKey: 'productId', header: 'Product ID' },
    ...(!hasLocationId ? [{ columnKey: 'warehouseId', header: 'Warehouse ID' }] : []),
    { columnKey: 'errorMsg', header: 'STATUS (REMARK)' },
  ], [hasLocationId]);

  return (
    <div className="inventoryContainer flex flex-col">
      <div className="relative" style={{backgroundColor: UI_CONSTANTS.DARK_BACKGROUND}}>
        <HeaderTitle
          headingLevel={Header.SectionTitle}
          title={UI_CONSTANTS.PAGE_TITLE}
          className="inventoryDataTableHeading pl-4 pr-4"
        />
        <span className="inventorySettingsIcon absolute h-8 w-8 rounded p-2">
          <Icon icon="settings" isDark={true} size="small" className="h-4 w-4" data-testid="settings-icon" />
        </span>

        <div className="px-4">
          <CustomDataTable
            columns={transformedColumns}
            records={inventoryData?.data || []}
            isLoading={inventoryResult.isLoading}
            pageSize={DEFAULT_TABLE_PAGE_SIZE}
            totalRecords={inventoryData?.totalRecords || 0}
            setPageIndex={handlePageIndexChange}
            handleSorting={handleSorting}
            setSearchArry={setSearchArry}
            onResetAll={() => {
              resetAllBtnClicked()
              handleResetAll()
            }}
            onRowSelect={handleRowSelect}
            actions={tableActions}
            showActions={true}
            isSelectAll={isSelectAll}
            setSelectAll={setSelectAll}
            defaultSortConfig={{key: 'productId', direction: 'desc'}}
          />
        </div>
      </div>

      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData.data}
        totalRecords={uploadHistoryData.totalRecords}
        mainColumns={columns}
        subColumns={dynamicSubColumns}
        fetchSubRows={fetchUploadErrors}
        isDark={true}
        setPageIndex={onUploadHistoryPageIndexChange}
        data-testid="upload-history-modal"
      />
    </div>
  )
}

export default InventoryList
